import { useCreateDepositAmendment } from '@services/amendments/mutations/useCreateDepositAmendment'
import { useDepositAmendmentPreview } from '@services/amendments/mutations/useRentAmendmentPdf'
import { PRICE_FIELDS, type Prices } from '@services/amendments/types'
import { Eye, FileSignature, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RentModel } from 'types/api'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

import { money, round2 } from '../utils'

// Zák. č. 98/2014 Z. z. — at most three monthly payments (rent + services).
const DEPOSIT_MAX_MONTHS = 3

type Props = {
  flatId: string
  /** The flat's deposit today. */
  current: number
  /** The flat's monthly amounts — they set the legal deposit limit. */
  prices: Prices
  /** The deposit payout to the landlord generated so far, if any. */
  depositPayment?: RentModel
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * A landlord amendment that changes only the security deposit (flat contract
 * Art. IV 9). Signed by every party in DocuSeal and effective on signing; the
 * difference is settled within 10 days as the amendment text says — no
 * payment is created for it.
 */
export function DepositAmendmentDialog(props: Props) {
  const { flatId, current, prices, depositPayment, open, onOpenChange } = props
  const { t } = useTranslation('amendments')
  const { confirm, dialog } = useConfirm()
  const createAmendment = useCreateDepositAmendment()
  const preview = useDepositAmendmentPreview()

  const [value, setValue] = useState('')
  const [lastOpen, setLastOpen] = useState(false)
  if (open !== lastOpen) {
    setLastOpen(open)
    if (open) setValue(String(round2(current)))
  }

  const deposit = round2(Number(value) || 0)
  const limit = round2(
    DEPOSIT_MAX_MONTHS *
      PRICE_FIELDS.reduce((acc, field) => acc + (prices[field] || 0), 0)
  )
  const diff = round2(deposit - current)
  const overLimit = deposit > limit
  const invalid = value === '' || deposit < 0 || overLimit || diff === 0

  const body = () => ({ flat: flatId, deposit })

  const send = async () => {
    const ok = await confirm({
      title: t('confirm_title'),
      description: t('confirm_description'),
      confirmText: t('confirm_send'),
      cancelText: t('cancel')
    })
    if (!ok) return
    createAmendment.mutate(body(), { onSuccess: () => onOpenChange(false) })
  }

  return (
    <>
      {dialog}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle>{t('title_deposit')}</DialogTitle>
                <p className="m-0 mt-1 text-sm text-muted-foreground">
                  {t('hint_deposit')}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_8rem] items-center gap-3">
              <Label className="text-sm">{t('deposit_current')}</Label>
              <span className="px-3 text-sm text-muted-foreground">
                {money(current)}
              </span>
            </div>
            <p className="m-0 pb-3 text-xs text-muted-foreground">
              {depositPayment
                ? t(
                    depositPayment.paid
                      ? 'deposit_payment_paid'
                      : 'deposit_payment_unpaid',
                    { amount: money(Number(depositPayment.rent ?? 0)) }
                  )
                : t('deposit_payment_none')}
            </p>
            <div className="grid grid-cols-[1fr_8rem] items-center gap-3">
              <Label className="text-sm" htmlFor="deposit-new">
                {t('deposit_new')}
              </Label>
              <Input
                id="deposit-new"
                type="number"
                min={0}
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={cn(
                  diff !== 0 && 'border-primary font-semibold',
                  overLimit && 'border-destructive'
                )}
              />
            </div>
            <p
              className={cn(
                'm-0 text-xs',
                overLimit ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {t('deposit_limit', { limit: money(limit) })}
            </p>
            {diff !== 0 && !overLimit && (
              <div className="rounded-xl bg-primary/5 px-3 py-2 text-sm text-ink">
                {t(diff > 0 ? 'deposit_top_up' : 'deposit_refund', {
                  amount: money(Math.abs(diff))
                })}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghostDark"
              onClick={() => preview.mutate(body())}
              disabled={invalid || preview.isPending}
            >
              <Eye className="h-4 w-4" />
              {t('preview')}
            </Button>
            <Button
              onClick={send}
              disabled={invalid || createAmendment.isPending}
            >
              <FileSignature className="h-4 w-4" />
              {t('send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
